/** Square portrait frames. Inner window is a square so the art cover-crops. */

export type AvatarFrameId =
  | 'gold'
  | 'silver'
  | 'obsidian'
  | 'ivory'
  | 'crimson'
  | 'azure'
  | 'bronze'
  | 'emerald'
  | 'last-call'
  | 'league-duelist'
  | 'league-challenger'
  | 'league-champion'
  | 'league-king'

export interface AvatarFrameDef {
  id: AvatarFrameId
  metal: string
  light: string
  dark: string
  ink: string
  accent: string
}

export const AVATAR_FRAMES: readonly AvatarFrameDef[] = [
  {
    id: 'gold',
    metal: '#d4af37',
    light: '#fde68a',
    dark: '#7a5414',
    ink: '#3f2e08',
    accent: '#fff6c8',
  },
  {
    id: 'silver',
    metal: '#c0c7d1',
    light: '#f4f6f8',
    dark: '#4b5563',
    ink: '#1f2937',
    accent: '#e5e7eb',
  },
  {
    id: 'obsidian',
    metal: '#2a2e38',
    light: '#6b7280',
    dark: '#0b0d12',
    ink: '#e8c547',
    accent: '#d4af37',
  },
  {
    id: 'ivory',
    metal: '#e8dcc4',
    light: '#f7f1e3',
    dark: '#8a7a5c',
    ink: '#4a3f2c',
    accent: '#c9a227',
  },
  {
    id: 'crimson',
    metal: '#9f1239',
    light: '#fb7185',
    dark: '#4c0519',
    ink: '#fde68a',
    accent: '#fecdd3',
  },
  {
    id: 'azure',
    metal: '#1d4ed8',
    light: '#7dd3fc',
    dark: '#0c2744',
    ink: '#e8c547',
    accent: '#bae6fd',
  },
  {
    id: 'bronze',
    metal: '#b87333',
    light: '#e8b87a',
    dark: '#5c3a18',
    ink: '#2a1808',
    accent: '#f5d0a6',
  },
  {
    id: 'emerald',
    metal: '#047857',
    light: '#6ee7b7',
    dark: '#064e3b',
    ink: '#fde68a',
    accent: '#a7f3d0',
  },
  {
    id: 'last-call',
    metal: '#d7eef8',
    light: '#f7fdff',
    dark: '#0e3a66',
    ink: '#071422',
    accent: '#3ec6ff',
  },
  {
    id: 'league-duelist',
    metal: '#b87333',
    light: '#e8b87a',
    dark: '#5c3a18',
    ink: '#2a1808',
    accent: '#f5d0a6',
  },
  {
    id: 'league-challenger',
    metal: '#c0c7d1',
    light: '#f4f6f8',
    dark: '#1e3a8a',
    ink: '#0c2744',
    accent: '#7dd3fc',
  },
  {
    id: 'league-champion',
    metal: '#d4af37',
    light: '#fde68a',
    dark: '#7a5414',
    ink: '#4c0519',
    accent: '#e11d48',
  },
  {
    id: 'league-king',
    metal: '#1c1a24',
    light: '#a78bfa',
    dark: '#07060b',
    ink: '#e8c547',
    accent: '#d4af37',
  },
]

export const AVATAR_FRAME_IDS: readonly AvatarFrameId[] = AVATAR_FRAMES.map(
  (frame) => frame.id,
)

export const DEFAULT_FRAME_ID: AvatarFrameId = 'gold'

const BY_ID = new Map(AVATAR_FRAMES.map((frame) => [frame.id, frame]))

export function isAvatarFrameId(id: string): id is AvatarFrameId {
  return BY_ID.has(id as AvatarFrameId)
}

/**
 * Keep a catalog frame; otherwise gold.
 */
export function coerceAvatarFrameId(raw: unknown): AvatarFrameId {
  if (typeof raw === 'string' && isAvatarFrameId(raw)) return raw
  return DEFAULT_FRAME_ID
}

export function avatarFrameById(id: string): AvatarFrameDef {
  return BY_ID.get(coerceAvatarFrameId(id)) ?? AVATAR_FRAMES[0]!
}
